import { supabase } from "../supabase/SupabaseClient";
import { createRow } from "./supabaseRealtime";
import { actual } from "./quien";
import { licenciasDe } from "./licencias";
import { traerTodas } from "./paginar";
import { hoyAr, vigentesPorDni } from "../shared/nominaSemaforo";
import {
  BUCKET,
  lineaDeTiempo,
  rutaDe,
} from "../shared/rrhhNovedades";

// Se re-exportan para que la pantalla importe de un solo lado.
export { BUCKET, TIPOS, lineaDeTiempo, rotuloTipo, rutaDe } from "../shared/rrhhNovedades";
export {
  SEMAFORO_NOMINA,
  comoSemaforoNomina,
  comoSeLeeLaNovedad,
  estaVigente,
  hoyAr,
  semaforoPersona,
  vigentesPorDni,
} from "../shared/nominaSemaforo";

/* Novedades de RRHH: qué le pasó a cada empleado, con el papel que lo prueba.
 *
 * Pedido de Nico (25/08/2026): «un sector de novedades. Ahí se dejan registro
 * y documentación de una novedad como enfermedad, suspensión, muerte de un
 * familiar. La idea es que puedas consultar las novedades por empleado».
 *
 * ── NO CONFUNDIR CON `services/novedades.js` ────────────────────────────────
 *
 * Ése es el INICIO del hub y arranca diciendo que NO HAY una tabla
 * `novedades`, porque sus tarjetas se DERIVAN de datos que ya existen —nadie
 * escribe novedades a mano y una pantalla que hay que alimentar se muere—.
 * Esto es lo contrario y por eso puede existir: que a alguien se le murió el
 * padre no lo sabe ningún sistema, no hay de dónde derivarlo.
 *
 * ── QUIÉN LEE Y QUIÉN ESCRIBE LO DECIDE LA BASE ─────────────────────────────
 *
 * `ver.rrhh` para leer y `cargar.rrhh` para cargar, el MISMO par que
 * `licencias`: es el mismo tipo de dato y quien puede cargar un parte de
 * enfermo puede cargar una novedad. Sin la llave, Supabase devuelve cero filas
 * o rechaza el insert; la pantalla sólo decide cómo contarlo.
 *
 * ── ANULAR NO BORRA ─────────────────────────────────────────────────────────
 *
 * Una novedad mal cargada se anula y queda, con quién la anuló y cuándo. Acá
 * pesa más que en otras tablas: son suspensiones y partes médicos, y «¿quién
 * la sacó?» tiene que tener respuesta.
 */

const CAMPOS =
  "id,dni,tipo,desde,hasta,detalle,usuario,usuario_nombre," +
  "creado_en,anulado_en,anulado_por";

const CAMPOS_ADJ =
  "id,novedad_id,ruta,nombre_original,mime,bytes,usuario,creado_en";

/* `licencias` guarda el texto en `nota` y no en `detalle`. Sin esta lista
 * aparte, el semáforo pediría una columna que no existe y la consulta entera
 * volvería con error — o sea, sin amarillos y sin saber por qué. */
const CAMPOS_LIC =
  "id,dni,tipo,desde,hasta,nota,usuario,usuario_nombre," +
  "creado_en,anulado_en,anulado_por";

const soloDigitos = (dni) => String(dni ?? "").replace(/\D/g, "");

/* ── Leer ─────────────────────────────────────────────────────────────────── */

/* Las novedades de UNA persona. Trae las anuladas: en un historial, una
 * novedad que se cargó y se anuló es historia, no ruido. La pantalla las
 * marca. */
export async function novedadesDe(dni) {
  const d = soloDigitos(dni);
  if (!d) return [];
  const { data, error } = await supabase
    .from("rrhh_novedades")
    .select(CAMPOS)
    .eq("dni", d)
    .order("desde", { ascending: false })
    .limit(200);
  if (error) throw error;
  return data || [];
}

/* Todas las recientes, para la pantalla de RRHH. Un año, igual que licencias:
 * la pantalla es de trabajo, el archivo es la ficha de cada uno. */
export async function traerNovedades() {
  const hace1anio = new Date(Date.now() - 365 * 24 * 3600 * 1000)
    .toISOString()
    .slice(0, 10);
  const { data, error } = await supabase
    .from("rrhh_novedades")
    .select(CAMPOS)
    .gte("desde", hace1anio)
    .order("desde", { ascending: false })
    .limit(500);
  if (error) throw error;
  return data || [];
}

/* ── Las que están vigentes HOY, para el semáforo de la nómina ──────────────
 *
 * Ticket #148 (Nico): el círculo amarillo al lado del nombre de quien tiene
 * una novedad. La regla de qué cuenta como vigente está en
 * `shared/nominaSemaforo.js`; acá se traen las filas.
 *
 * ── SE FILTRA EN LA BASE Y NO ACÁ, Y NO ES POR VELOCIDAD ────────────────────
 *
 * `traerNovedades` y `traerLicencias` traen UN AÑO hacia atrás, que es lo
 * correcto para la pantalla de carga —es de trabajo, no de archivo—. Pero una
 * novedad abierta en 2025 y sin `hasta` SIGUE VIGENTE hoy, y con esa ventana
 * no vendría: el semáforo diría verde sobre alguien que está de licencia hace
 * un año y medio. Preguntando por la condición en vez de por una ventana de
 * fechas, el caso raro entra solo.
 *
 * ── Y DEVUELVE `{porDni, error}` ────────────────────────────────────────────
 *
 * Las otras dos tiran `throw`, que sirve en una pantalla que no puede seguir
 * sin ellas. Ésta sí puede: la nómina se muestra igual y lo que se pierde es
 * el amarillo. Un `throw` acá voltearía las 292 filas por un agregado — pero
 * el error se devuelve para que la pantalla lo diga, porque un semáforo que
 * mira de menos y no avisa muestra verdes que nadie verificó.
 */
export async function vigentesHoy() {
  const hoy = hoyAr();
  /* PAGINA, no `.limit(2000)`. El primer intento pedía un tope grande «con
     margen de sobra» y PostgREST corta en 1.000: habría traído mil y se
     habría callado, que es justo el error que documenta `paginar.js` y que
     `sinLimitesFalsos.test.js` frena. Hoy son cero filas y con cualquiera de
     las dos formas andaría — por eso hace falta el test y no el criterio. */
  const pedir = (tabla, campos) =>
    traerTodas(() =>
      supabase
        .from(tabla)
        .select(campos, { count: "exact" })
        .is("anulado_en", null)
        .lte("desde", hoy)
        .or(`hasta.is.null,hasta.gte.${hoy}`)
        .order("desde", { ascending: false }),
    );

  try {
    const [nov, lic] = await Promise.all([
      pedir("rrhh_novedades", CAMPOS),
      pedir("licencias", CAMPOS_LIC),
    ]);
    return { porDni: vigentesPorDni(nov, lic, hoy), error: "" };
  } catch (e) {
    const error = e?.message || "No se pudieron leer.";
    console.warn("[rrhh] no pude leer las novedades vigentes:", error);
    return { porDni: {}, error };
  }
}

/* Los adjuntos de varias novedades de una sola vez. En lote y no uno por
 * novedad: la ficha de alguien con quince novedades haría quince consultas
 * para dibujar una lista. */
export async function adjuntosDe(ids) {
  const lista = (ids || []).filter((x) => x != null);
  if (!lista.length) return {};
  const { data, error } = await supabase
    .from("rrhh_novedades_adjuntos")
    .select(CAMPOS_ADJ)
    .in("novedad_id", lista);
  if (error) throw error;
  const por = {};
  for (const a of data || []) (por[a.novedad_id] ||= []).push(a);
  return por;
}


/* Todo lo de una persona, ya mezclado. Las dos consultas en paralelo. */
export async function historialDe(dni) {
  const [nov, lic] = await Promise.all([
    novedadesDe(dni),
    licenciasDe(dni).catch(() => []),
  ]);
  return lineaDeTiempo(nov, lic);
}

/* ── Escribir ─────────────────────────────────────────────────────────────── */

/* Pasa por `createRow` para que quede sellada con quién la cargó. Una
 * suspensión anónima es justo la fila que un día nadie puede defender. */
export const crearNovedad = ({ dni, tipo, desde, hasta, detalle }) =>
  createRow("rrhh_novedades", null, {
    dni: soloDigitos(dni),
    tipo,
    desde,
    // Vacío es null y no "": una novedad sin fin —un fallecimiento— no tiene
    // `hasta`, y "" rompería el check de fechas sin decir por qué.
    hasta: hasta || null,
    detalle: (detalle || "").trim() || null,
  });

/* NO pasa por `updateRow` a propósito: `sellar` pisaría `usuario` con el
 * anulador y la fila perdería a quién la CARGÓ. El anulador va en su columna.
 * Igual que `anularLicencia`. */
export async function anularNovedad(id) {
  const quien = actual();
  const { data, error } = await supabase
    .from("rrhh_novedades")
    .update({
      anulado_en: new Date().toISOString(),
      anulado_por: quien?.mail || null,
    })
    .eq("id", id)
    .is("anulado_en", null)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/* ── Los archivos ─────────────────────────────────────────────────────────── */


/* Sube un archivo y lo anota. NO CONVIERTE NADA: el PDF de un certificado o
 * de un telegrama ES el registro —tiene la firma, el membrete y el sello— y
 * pasarlo a texto le saca justo lo que le da valor. Entra como vino.
 *
 * Si la subida sale bien pero la fila falla, se borra el archivo: un objeto
 * en el bucket que ninguna fila nombra no lo encuentra nadie y no lo borra
 * nadie. */
export async function subirAdjunto(novedadId, archivo) {
  if (!novedadId || !archivo) throw new Error("falta la novedad o el archivo");
  const ruta = rutaDe(novedadId, archivo.name);
  const { error: eSubir } = await supabase.storage
    .from(BUCKET)
    .upload(ruta, archivo, { contentType: archivo.type || undefined });
  if (eSubir) throw eSubir;

  const quien = actual();
  const { data, error } = await supabase
    .from("rrhh_novedades_adjuntos")
    .insert({
      novedad_id: novedadId,
      ruta,
      nombre_original: archivo.name || null,
      mime: archivo.type || null,
      bytes: archivo.size ?? null,
      usuario: quien?.mail || null,
    })
    .select(CAMPOS_ADJ)
    .single();
  if (error) {
    await supabase.storage.from(BUCKET).remove([ruta]).catch(() => {});
    throw error;
  }
  return data;
}

/* El link para mirarlo. Firmado y corto: el bucket es privado porque adentro
 * hay certificados médicos con nombre y diagnóstico. */
export async function linkAdjunto(ruta, segundos = 300) {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(ruta, segundos);
  if (error) throw error;
  return data?.signedUrl || null;
}

/* Sacar un adjunto SÍ borra, y es el único lugar donde eso es lo correcto:
 * alguien sube el certificado de otra persona y dejarlo «anulado pero
 * visible» sería exponer un dato médico ajeno para siempre. La novedad queda;
 * el archivo se va. */
export async function borrarAdjunto(adj) {
  if (!adj?.id) throw new Error("sin adjunto");
  const { error } = await supabase
    .from("rrhh_novedades_adjuntos")
    .delete()
    .eq("id", adj.id);
  if (error) throw error;
  if (adj.ruta) await supabase.storage.from(BUCKET).remove([adj.ruta]);
}
